import { useCallback, useRef, useState } from 'react';
import {
  buildCreateAssessmentStage,
  buildMoveAssessmentStage,
  buildRemoveAssessmentStage,
} from '../../../lib/services/assessment-stage.service';

export const useStageStructureActions = () => {
  const { createAssessmentStage } = buildCreateAssessmentStage();
  const { moveAssessmentStage } = buildMoveAssessmentStage();
  const { removeAssessmentStage } = buildRemoveAssessmentStage();
  const inFlight = useRef(false);
  const [isChangingStructure, setChangingStructure] = useState(false);
  const [structureError, setStructureError] = useState('');

  const perform = useCallback(async (action: () => Promise<unknown>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setChangingStructure(true);
    setStructureError('');
    try {
      await action();
    } catch (error) {
      setStructureError(error instanceof Error ? error.message : 'Unable to change the format. Please try again.');
    } finally {
      inFlight.current = false;
      setChangingStructure(false);
    }
  }, []);

  const createStage = useCallback((data: Pick<IAssessmentStage, 'type' | 'order' | 'config'> & { ageGroupId: string }) =>
    perform(() => createAssessmentStage(data)), [createAssessmentStage, perform]);
  const moveStage = useCallback((stage: IAssessmentStage, direction: 'UP' | 'DOWN') =>
    perform(() => moveAssessmentStage(stage, direction)), [moveAssessmentStage, perform]);
  const removeStage = useCallback((_id: string) =>
    perform(() => removeAssessmentStage(_id)), [removeAssessmentStage, perform]);

  return { createStage, moveStage, removeStage, isChangingStructure, structureError };
};
